import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Trash2, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable branding uploader.
 * Props: kind ("institutions" | "campuses"), id, name, current { logo_url, background_url }, onClose
 */
export default function BrandingDialog({ kind, id, name, current = {}, onClose }) {
  const [data, setData] = useState({
    logo_url: current.logo_url || null,
    background_url: current.background_url || null,
  });
  const qc = useQueryClient();

  const refresh = () => {
    // Re-fetch parent listing + any login-context cached
    qc.invalidateQueries({ queryKey: ["institutions"] });
    qc.invalidateQueries({ queryKey: ["campuses"] });
    qc.invalidateQueries({ queryKey: ["public-insts"] });
    qc.invalidateQueries({ queryKey: ["public-campuses"] });
    qc.invalidateQueries({ queryKey: ["login-ctx"] });
  };

  const handleUpload = async (assetType, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post(`/branding/${kind}/${id}`, fd, {
        params: { asset_type: assetType },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData((d) => ({ ...d, [`${assetType}_url`]: res.url }));
      toast.success(`${assetType === "logo" ? "Logo" : "Background"} uploaded`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
      throw e;
    }
  };

  const handleClear = async (assetType) => {
    if (!window.confirm(`Remove the ${assetType}?`)) return;
    try {
      await api.delete(`/branding/${kind}/${id}`, { params: { asset_type: assetType } });
      setData((d) => ({ ...d, [`${assetType}_url`]: null }));
      toast.success(`${assetType} cleared`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Clear failed");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="branding-dialog">
        <DialogHeader>
          <DialogTitle>Branding · {name}</DialogTitle>
          <DialogDescription>
            Upload a logo and a background image. These show up on the login page Stage 2 when users
            pick this {kind === "institutions" ? "institution" : "campus"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <AssetSlot
            label="Logo"
            assetType="logo"
            url={data.logo_url}
            aspect="square"
            kind={kind}
            onUpload={handleUpload}
            onClear={handleClear}
          />
          <AssetSlot
            label="Background image"
            assetType="background"
            url={data.background_url}
            aspect="wide"
            kind={kind}
            onUpload={handleUpload}
            onClear={handleClear}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetSlot({ label, assetType, url, aspect, kind, onUpload, onClear }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fullUrl = url ? (url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`) : null;

  const pick = () => inputRef.current?.click();
  const submit = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    setBusy(true);
    try { await onUpload(assetType, file); } catch { /* error already toasted */ } finally { setBusy(false); }
  };

  return (
    <div data-testid={`branding-slot-${assetType}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider">{label}</div>
        {url && (
          <Button size="sm" variant="ghost" onClick={() => onClear(assetType)} data-testid={`branding-clear-${assetType}`}>
            <Trash2 className="h-3 w-3 mr-1.5 text-destructive" /> Remove
          </Button>
        )}
      </div>
      <div
        onClick={pick}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); submit(e.dataTransfer.files?.[0]); }}
        className={`relative border-2 border-dashed rounded-lg cursor-pointer transition-all overflow-hidden ${
          dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-muted/40"
        } ${aspect === "square" ? "aspect-square max-w-[200px]" : "aspect-[16/9]"}`}
        data-testid={`branding-drop-${assetType}`}
      >
        {fullUrl ? (
          <>
            <img src={fullUrl} alt={label} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition flex items-center justify-center opacity-0 hover:opacity-100">
              <div className="text-white text-xs font-medium flex items-center gap-1.5">
                <Upload className="h-3.5 w-3.5" /> Replace
              </div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-2 p-4">
            <ImgIcon className="h-7 w-7 opacity-50" />
            <div className="text-xs font-medium">Click or drop to upload</div>
            <div className="text-[10px]">PNG / JPG · max 8MB</div>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`branding-input-${assetType}`}
        onChange={(e) => submit(e.target.files?.[0])}
      />
    </div>
  );
}
